/**
 * The matched Mia session as the record Smart Embed shows the agent, and carries
 * into `zcc-phone-call-log`. Zoom's contract is five fields, so the caller, the
 * property and the case have to share them.
 */
import type { CallerContextMatch } from './caller-context-match'

export interface ZoomContactRecord {
  id: string
  name: string
  phone: string
  email: string
  entity: string
}

/** `file-stub-case` writes these literally, so they would identify nobody. */
const PLACEHOLDER_NAME_PARTS = new Set(['unknown', 'caller'])

function realNamePart(value: string | null): string | null {
  const trimmed = value?.trim()
  if (!trimmed || PLACEHOLDER_NAME_PARTS.has(trimmed.toLowerCase())) return null
  return trimmed
}

function displayName(match: CallerContextMatch): string {
  const { callerFirstName, callerLastName, customerName } = match.session
  const caller = [realNamePart(callerFirstName), realNamePart(callerLastName)]
    .filter(Boolean)
    .join(' ')

  // The property outranks the caller: a stub has one and no name.
  return (
    [caller, customerName?.trim()].filter(Boolean).join(' · ') || 'Mia AI call'
  )
}

/**
 * How a match is named across the Zoom boundary. `zcc-screen-pop` quotes it
 * back, so the panel resolves the agent's pick through the same function.
 *
 * The call, not the case: a case filed mid-call would rename the record under
 * a pick the agent had already made.
 */
export function contactRecordId(match: CallerContextMatch): string {
  return match.session.callSid
}

/**
 * An empty array is Zoom's documented "no match". More than one entry makes Zoom
 * show its chooser and fire `zcc-screen-pop` with whichever the agent picks.
 */
export function buildIncomingPhoneResponse(
  matches: CallerContextMatch[],
  incomingPhoneNumber: string
): ZoomContactRecord[] {
  return matches.map((match) => {
    const { caseNumber } = match.session
    return {
      id: contactRecordId(match),
      name: displayName(match),
      phone: incomingPhoneNumber,
      email: '',
      entity:
        match.state === 'cased' && caseNumber
          ? `Mia · ${caseNumber}`
          : 'Mia · case pending'
    }
  })
}
